import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LockProvider, useLock } from './context/LockContext';
import { ThemeProvider } from './context/ThemeContext';
import { VerticalProvider } from './context/VerticalContext';
import { WorkModeProvider } from './context/WorkModeContext';
import { SidebarProvider, useSidebar } from './context/SidebarContext';
import { TourProvider } from './context/TourContext';
import { BreadcrumbProvider } from './context/BreadcrumbContext';
import { storage, migrateLocalStorageKeys } from './utils/storage';
import { getBasename } from './utils/basename';
import Breadcrumbs from './components/Breadcrumbs';
import { SubscriptionProvider, useSubscription } from './context/SubscriptionContext';
import TourOverlay from './components/TourOverlay';
import ProtectedRoute from './components/ProtectedRoute';
import RequireRestaurant from './components/RequireRestaurant';
import LockScreen from './components/LockScreen';
import OnboardingWizard from './components/OnboardingWizard';
import { Header, ActivityBar, DockPanel } from './components/layout';
import { ChatProvider } from './context/ChatContext';
import ChatWidget from './components/ChatWidget';
import { NotificationPrompt } from './components/NotificationPrompt';
import { usePushNotifications } from './hooks/usePushNotifications';
import Dashboard from './pages/Dashboard';
import AgendaPage from './pages/AgendaPage';
import BookingsPage from './pages/BookingsPage';
import ProfessionalsPage from './pages/ProfessionalsPage';
import ServicesPage from './pages/ServicesPage';
import CoveragesPage from './pages/CoveragesPage';
import CoverageMatrixPage from './pages/CoverageMatrixPage';
import MetricsPage from './pages/MetricsPage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailsPage from './pages/CustomerDetailsPage';
import ConversationsPage from './pages/ConversationsPage';
import SubscriptionPage from './pages/SubscriptionPage';
import PlanSelectionPage from './pages/PlanSelectionPage';
import UsersPage from './pages/UsersPage';
import UserFormPage from './pages/UserFormPage';
import SettingsPage from './pages/SettingsPage';
import SetupWizardPage from './pages/SetupWizardPage';
import NotFoundPage from './pages/NotFoundPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import NewPasswordPage from './pages/NewPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import TermsPage from './pages/TermsPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import CookieConsent from './components/CookieConsent';
import { useTranslation } from 'react-i18next';
import './App.css';

// NER-3145: lazy — OrgMetricsPage arrastra Recharts (~150kB gzip). Se carga en
// su propio chunk sólo al visitar /analytics para no engordar el bundle inicial.
const OrgMetricsPage = lazy(() => import('./pages/OrgMetricsPage'));

// NER-2688: chat de soporte (mismo widget que Resto admin, portado a Citame).
// Gateado por env hasta que el backend exponga `/chat/messages` en el gateway
// Citame con tenancy por claim `custom:businessId` + tag `source='citame'`
// (ver subtarea Backend). Default OFF para no renderear un widget que pega a un
// endpoint inexistente. Se enciende con REACT_APP_SUPPORT_CHAT_ENABLED=true.
const SUPPORT_CHAT_ENABLED = process.env.REACT_APP_SUPPORT_CHAT_ENABLED === 'true';

// Inner component that uses sidebar context
function AppContent({ pushNotifications }) {
  const { isSecondarySidebarVisible } = useSidebar();
  const { t } = useTranslation();
  const [isDockExpanded, setIsDockExpanded] = useState(true);

  // NER-1563: the right dock now hosts Citame quick actions and is always
  // available (no Resto work-mode gating). It only collapses to the icon bar.
  const layoutClass = [
    'app-layout-new',
    isSecondarySidebarVisible ? 'sidebar-pinned' : '',
    !isDockExpanded ? 'dock-collapsed' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={layoutClass}>
      <a href="#main-content" className="skip-link">
        {t('common.skipToContent')}
      </a>
      <Header />
      <ActivityBar />
      <DockPanel isExpanded={isDockExpanded} onToggle={setIsDockExpanded} />
      <TourOverlay />
      {/* Chat de soporte flotante — solo post-login (AppContent vive dentro de
          ProtectedRoute), mobile responsive vía ChatWidget.css (NER-2688). */}
      {SUPPORT_CHAT_ENABLED && (
        <ChatProvider>
          <ChatWidget />
        </ChatProvider>
      )}
      {/* Prompt de notificaciones push */}
      <NotificationPrompt
        isSupported={pushNotifications.isSupported}
        isSubscribed={pushNotifications.isSubscribed}
        permission={pushNotifications.permission}
        isLoading={pushNotifications.isLoading}
        error={pushNotifications.error}
        onSubscribe={pushNotifications.subscribe}
      />
      <main id="main-content" className="main-content-new" tabIndex="-1">
        <Breadcrumbs />
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/agenda" element={<AgendaPage />} />
          <Route path="/bookings" element={<BookingsPage />} />
          <Route path="/professionals" element={<ProfessionalsPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/coverages" element={<CoveragesPage />} />
          <Route path="/coverage-matrix" element={<CoverageMatrixPage />} />
          <Route path="/metrics" element={<MetricsPage />} />
          {/* NER-3145: métricas per-tenant (dueño-facing). OWNER/ADMIN. */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute allowedRoles={['OWNER', 'ADMIN']}>
                <Suspense fallback={<div className="loading">{t('common.loading')}</div>}>
                  <OrgMetricsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:customerId" element={<CustomerDetailsPage />} />
          <Route path="/conversations" element={<ConversationsPage />} />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <SubscriptionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/plan-selection"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <PlanSelectionPage />
              </ProtectedRoute>
            }
          />
          <Route path="/settings" element={<SettingsPage />} />
          {/* NER-3256: Setup Wizard onboarding. SuperAdmin only — el guard vive
              dentro de SetupWizardPage (email @nerdo.solutions); ProtectedRoute
              aquí sólo asegura sesión + acceso al panel. */}
          <Route
            path="/admin/setup-wizard"
            element={
              <ProtectedRoute>
                <SetupWizardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/new"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <UserFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/edit/:userId"
            element={
              <ProtectedRoute allowedRoles={['OWNER']}>
                <UserFormPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>
    </div>
  );
}

function AppLayout() {
  const [restaurantId, setRestaurantId] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const { isLocked } = useLock();
  const { user, organization } = useAuth();
  const {
    hasActiveSubscription,
    initialized,
    loading: subscriptionLoading,
    error: subscriptionError,
    refreshSubscription,
  } = useSubscription();

  // Obtener restaurantId de localStorage
  useEffect(() => {
    const getRestaurantId = () => {
      const id = storage.get('selectedRestaurantId');
      setRestaurantId(id);
    };
    getRestaurantId();
    window.addEventListener('storage', getRestaurantId);
    const handleRestaurantChange = () => getRestaurantId();
    window.addEventListener('restaurantChanged', handleRestaurantChange);
    return () => {
      window.removeEventListener('storage', getRestaurantId);
      window.removeEventListener('restaurantChanged', handleRestaurantChange);
    };
  }, []);

  // Verificar si debe mostrar el onboarding wizard
  // Reactivo: también oculta el wizard si la suscripción se carga tarde (post-login race condition)
  useEffect(() => {
    // Esperar a que la verificación termine (no decidir mientras está cargando)
    if (!initialized || subscriptionLoading || !user || !organization) return;

    const hasSub = hasActiveSubscription();
    console.log('[AppLayout] Verificación de suscripción:', {
      hasActiveSubscription: hasSub,
      subscriptionError,
      showOnboarding,
      user: user?.username,
      organization: organization?.organizationId,
    });

    if (hasSub && showOnboarding) {
      // Suscripción cargó tarde — ocultar wizard
      console.log('[AppLayout] Suscripción detectada, ocultando wizard');
      setShowOnboarding(false);
    } else if (!hasSub && !subscriptionError && !showOnboarding && !initialCheckDone) {
      // No tiene suscripción — mostrar wizard (solo la primera vez)
      console.log('[AppLayout] No tiene suscripción activa, mostrando wizard');
      setShowOnboarding(true);
      setInitialCheckDone(true);
    }
  }, [
    initialized,
    subscriptionLoading,
    user,
    organization,
    hasActiveSubscription,
    subscriptionError,
    showOnboarding,
    initialCheckDone,
  ]);

  // Hook de Push Notifications
  const pushNotifications = usePushNotifications(restaurantId, 'admin', user?.userId);

  // Manejador para cuando se completa el onboarding
  const handleOnboardingComplete = async () => {
    console.log('[AppLayout] Onboarding completado, refrescando suscripción...');
    // Refrescar el estado de suscripción para asegurar que se actualice
    await refreshSubscription();
    setShowOnboarding(false);
  };

  return (
    <>
      {isLocked ? (
        <LockScreen />
      ) : (
        <>
          {/* Mostrar wizard de onboarding si no tiene suscripción */}
          <OnboardingWizard isOpen={showOnboarding} onComplete={handleOnboardingComplete} />

          <RequireRestaurant>
            <WorkModeProvider>
              <SidebarProvider>
                <TourProvider>
                  <BreadcrumbProvider>
                    <AppContent pushNotifications={pushNotifications} />
                  </BreadcrumbProvider>
                </TourProvider>
              </SidebarProvider>
            </WorkModeProvider>
          </RequireRestaurant>
        </>
      )}
    </>
  );
}

// Run one-time localStorage key migration (un-prefixed → admin_ prefixed)
migrateLocalStorageKeys();

// Detect basename dynamically: path-based CloudFront hosting serves the same
// build under distinct prefixes — /citame/admin (Citame) and /consultame/admin
// (Consultame). Shared with the 401 redirect in services/api.js so a forced
// re-login stays inside the active vertical (NER-4111).
const basename = getBasename();

function App() {
  return (
    <VerticalProvider>
      <ThemeProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <LockProvider>
              <Router basename={basename}>
                <CookieConsent />
                <Routes>
                  {/* Rutas públicas */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/new-password" element={<NewPasswordPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />

                  {/* Rutas protegidas */}
                  <Route
                    path="/*"
                    element={
                      <ProtectedRoute>
                        <AppLayout />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
              </Router>
            </LockProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProvider>
    </VerticalProvider>
  );
}

export default App;
